import { useMemo, useRef } from "react";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import {
	aggridHeaderClass,
	commonCodeCellStyle,
} from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
} from "src/utils/grid/gridStyleUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";

import {
	defaultTQC1060_R01DetailDto,
	TQC1060_R01DetailDto,
} from "./Dto_TQC1060_R01";
import { useTQC1060_R01DetailCellChange } from "./hooks/TQC1060_R01DetailCellChange";
import { useTQC1060_R01Store } from "./Store_TQC1060_R01";

const COLUMN_STATE_KEY = "TQC1060_R01-detail-column-state";
const EDITABLE_COLUMNS = [
	"inspectValue1_1",
	"inspectValue1_2",
	"inspectValue1_3",
	"inspectValue1_4",
	"inspectValue1_5",
	"remark",
];

export const DetailTable_TQC1060_R01 = () => {
	const {
		TQC1060_R01DetailList,
		setTQC1060_R01DetailList,
		updateTQC1060_R01DetailList,
		originalTQC1060_R01DetailList,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useTQC1060_R01Store();
	const gridRef = useRef<AgGridReact<TQC1060_R01DetailDto>>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const { getRowId } = useGridSettingContent<TQC1060_R01DetailDto>({
		gridRef,
		key: "valueIndex",
		gridData: TQC1060_R01DetailList,
		defaultGridData: defaultTQC1060_R01DetailDto,
		selectedRow: undefined,
		originalGridData: originalTQC1060_R01DetailList,
		updateRow: (index, data) =>
			updateTQC1060_R01DetailList(Number(index), data),
	});
	const { handleCellChange } = useTQC1060_R01DetailCellChange({
		updateTQC1060_R01DetailList,
		originalTQC1060_R01DetailList,
	});
	const { handleCellKeyDown, handleCellClick } =
		useDetailTableEvents<TQC1060_R01DetailDto>({
			detailList: TQC1060_R01DetailList,
			editableColumns: EDITABLE_COLUMNS,
			keyDownConfig: { enableArrowNavigation: true },
		});

	const valueColumn = (
		field: keyof TQC1060_R01DetailDto,
		headerName: string,
	): ColDef<TQC1060_R01DetailDto> => ({
		headerName,
		field,
		width: 90,
		editable: true,
		headerClass: aggridHeaderClass,
		cellStyle: commonCodeCellStyle,
	});

	const columnDefs: ColDef<TQC1060_R01DetailDto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: "검사항목",
				field: "inspItemNm",
				width: 160,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
			},
			{
				headerName: "기준값",
				field: "specText",
				width: 200,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
			},
			{
				headerName: "결과유형",
				field: "valueTypeNm",
				width: 90,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
			},
			valueColumn("inspectValue1_1", "검사값1"),
			valueColumn("inspectValue1_2", "검사값2"),
			valueColumn("inspectValue1_3", "검사값3"),
			valueColumn("inspectValue1_4", "검사값4"),
			valueColumn("inspectValue1_5", "검사값5"),
			{
				headerName: "비고",
				field: "remark",
				width: 100,
				editable: true,
				headerClass: aggridHeaderClass,
			},
		],
		[],
	);

	return (
		<GridContextWrapper<TQC1060_R01DetailDto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={TQC1060_R01DetailList}
			setPaddedGridData={setTQC1060_R01DetailList}
			initSelected={() => undefined}
			defaultRowData={defaultTQC1060_R01DetailDto}
			allSelected={() => undefined}
			baseColumnDefs={columnDefs}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
		>
			<Grid_Excel<TQC1060_R01DetailDto>
				ref={gridRef}
				rowData={TQC1060_R01DetailList}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				onCellClicked={handleCellClick}
				onCellValueChanged={handleCellChange}
				onCellKeyDown={handleCellKeyDown}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setDetailColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
			/>
		</GridContextWrapper>
	);
};
